"use client";

import FeatureTemplate from "@/components/feature-template";
import type {
  DetailedFeatureItem,
  FeatureHeroTab,
  TestimonialItem,
} from "@/components/feature-template/types";
import { BellRing, RefreshCw, Rows3 } from "lucide-react";
import { faqs } from "./content";

const IMG = "/assets/images/features/jobtracker-ai";
const APP = "/jobs/track";

// ============================================================================
// HERO TABS
// ============================================================================

const heroFeatures: FeatureHeroTab[] = [
  {
    id: "kanban",
    name: "Kanban Board",
    title: "Your Whole Search at a Glance",
    description:
      "Every application sits in a column from Saved to Accepted. Drag a card to update its stage and see exactly where each opportunity stands.",
    imageSrcUrl: `${IMG}/hero-kanban.png`,
    primaryActionText: "Open My Tracker",
    primaryActionLink: APP,
    secondaryActionText: "See How It Works",
    secondaryActionLink: "/how-to-use",
  },
  {
    id: "ai-insights",
    name: "AI Insights",
    title: "Know What to Do Next",
    description:
      "AI Insights flags applications that need a follow-up, spots where you get the most replies and reminds you which interviews to prepare for.",
    imageSrcUrl: `${IMG}/hero-ai-insights.png`,
    primaryActionText: "Open My Tracker",
    primaryActionLink: APP,
    secondaryActionText: "See How It Works",
    secondaryActionLink: "/how-to-use",
  },
  {
    id: "stats",
    name: "Stats",
    title: "Measure What Is Working",
    description:
      "Response, interview, offer and rejection rates, applications per week and a breakdown by status. Adjust your approach based on numbers.",
    imageSrcUrl: `${IMG}/hero-stats.png`,
    primaryActionText: "Open My Tracker",
    primaryActionLink: APP,
    secondaryActionText: "See How It Works",
    secondaryActionLink: "/how-to-use",
  },
];

// ============================================================================
// FEATURE GROUP 1 (dark)
// ============================================================================

const featureGroup1: DetailedFeatureItem[] = [
  {
    id: "follow-ups",
    title: "Follow-Ups That Never Slip",
    subtitle: "STAY ON THE RECRUITER'S RADAR",
    description:
      "Set a follow-up date and priority on every application. The ones due this week surface on their own, with notes and the details you need to write back.",
    benefits: [
      "Next follow-up date on every card",
      "High, medium and low priority",
      "Notes for referrals and recruiters",
      "Due-this-week list",
    ],
    primaryCTA: "Set Up Follow-Ups",
    primaryLink: APP,
    imageSrcUrl: `${IMG}/follow-ups.png`,
    icon: <BellRing className="w-4 h-4" />,
    stats: "Reminders built in",
  },
  {
    id: "list-view",
    title: "Every Detail in List View",
    subtitle: "SORT, FILTER, UPDATE",
    description:
      "Switch to the list to see applied dates, follow-ups, priority and status side by side. Filter by stage and change status without leaving the row.",
    benefits: [
      "Filter by stage",
      "Search by role or company",
      "Inline status changes",
      "Overdue follow-ups highlighted",
    ],
    primaryCTA: "Open My Tracker",
    primaryLink: APP,
    secondaryCTA: "Find Jobs to Track",
    secondaryLink: "/features/ai-job-match",
    imageSrcUrl: `${IMG}/list-view.png`,
    icon: <Rows3 className="w-4 h-4" />,
    stats: "Kanban · List · Stats",
    orientation: "reversed",
  },
];

// ============================================================================
// FEATURE GROUP 2 (light)
// ============================================================================

const featureGroup2: DetailedFeatureItem[] = [
  {
    id: "auto-sync",
    title: "Filled In for You",
    subtitle: "NO SPREADSHEETS, NO COPY-PASTE",
    description:
      "Applications sent by Auto Apply land in Applied on their own. Track any Job Board listing in one click, or add a role from anywhere in seconds.",
    benefits: [
      "Auto Apply jobs added automatically",
      "One-click Track from the Job Board",
      "Quick manual add",
      "Role, company and link pre-filled",
    ],
    primaryCTA: "Start Tracking Free",
    primaryLink: APP,
    secondaryCTA: "Explore Auto Apply",
    secondaryLink: "/ai-agents/auto-apply",
    imageSrcUrl: `${IMG}/auto-sync.png`,
    icon: <RefreshCw className="w-4 h-4" />,
    stats: "Works with Auto Apply",
  },
];

// ============================================================================
// TESTIMONIALS (replace with real user quotes before publishing)
// ============================================================================

const testimonials: TestimonialItem[] = [
  {
    id: 1,
    text: "I had 40 applications across three spreadsheets. Now they are on one board, and the follow-up reminder got me a reply from a company I had forgotten about.",
    name: "Meera K.",
    role: "Marketing Specialist",
    company: "MindMyJob user",
    image:
      "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=600&h=800&fit=crop&q=80",
  },
  {
    id: 2,
    text: "The stats showed my response rate for senior roles was twice my rate for lead roles. I shifted focus that week and had two interviews by the next.",
    name: "Arjun M.",
    role: "Backend Engineer",
    company: "MindMyJob user",
    image:
      "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=600&h=800&fit=crop&q=80",
  },
  {
    id: 3,
    text: "Auto Apply adds every job to the tracker on its own, so I never wonder where I applied. I just open the board each morning.",
    name: "Ishita N.",
    role: "UX Designer",
    company: "MindMyJob user",
    image:
      "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=600&h=800&fit=crop&q=80",
  },
];

// ============================================================================
// PAGE
// ============================================================================

export default function JobsTrackerAIPageClient() {
  return (
    <FeatureTemplate
      type="type1"
      sections={[
        {
          type: "hero",
          data: {
            title: "Every Application.",
            titleHighlight: "One Pipeline.",
            subtitle:
              "JobTracker AI keeps every job you save and apply to on one board, reminds you when to follow up and shows which parts of your search are working. Free, and filled in for you.",
            ctaText: "Open My Tracker",
            ctaLink: APP,
            features: heroFeatures,
          },
        },
        {
          type: "features",
          data: {
            sectionTitle: "Stay on Top of Every Lead.",
            sectionSubtitle:
              "Dates, priorities and notes that keep good opportunities from going cold.",
            features: featureGroup1,
          },
        },
        { type: "features", data: { features: featureGroup2 } },
        {
          type: "testimonials",
          data: {
            title: "Organised Searches Get Offers",
            subtitle: "From job seekers who replaced their spreadsheets",
            testimonials,
          },
        },
        {
          type: "faq",
          data: {
            title: "Questions About JobTracker AI",
            subtitle: "How tracking works and what you can see.",
            faqs,
            ctaTitle: "Ready to organise your search?",
            ctaText: "Open My Tracker",
            ctaLink: APP,
          },
        },
      ]}
    />
  );
}
